import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ElementRef,
  HostBinding,
  OnDestroy,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  DoCheck,
  ViewChild,
  signal,
  AfterViewInit,
} from '@angular/core';

import {
  MatChipGrid,
  MatChipInputEvent,
  MatChipsModule,
} from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldControl } from '@angular/material/form-field';
import { AbstractControl, FormControl, NgControl } from '@angular/forms';
import { asapScheduler, observeOn, Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { IconManager } from '../../services/icon-manager.service';
import { iconCloseXs } from '../../svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';

const DUPLICATE_VALUE_ERROR = 'duplicateValues';
const DUPLICATE_INDEX_ERROR = 'duplicateIndices';
const MAX_LENGTH_ERROR = 'maxlength';

export enum SeparatorKeyCodes {
  ENTER = 13,
  COMMA = 188,
  SPACE = 32,
  TAB = 9,
  SEMICOLON = 186,
}

@Component({
  selector: 'twc-chip-input',
  standalone: true,
  imports: [MatChipsModule, MatIconModule],
  providers: [
    { provide: MatFormFieldControl, useExisting: ChipInputComponent },
  ],
  templateUrl: './chip-input.component.html',
  styleUrls: ['./chip-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipInputComponent
  implements MatFormFieldControl<string[]>, OnDestroy, DoCheck, AfterViewInit
{
  private _elementRef = inject(ElementRef);
  private _focusMonitor = inject(FocusMonitor);
  private _iconManager = inject(IconManager);
  private _destroyRef = inject(DestroyRef);
  private _cd = inject(ChangeDetectorRef);

  ngControl = inject(NgControl, { optional: true, self: true });

  @ViewChild('chipInput') private _chipInputRef: ElementRef<HTMLInputElement>;
  @ViewChild('chipGrid') private _chipGrid: MatChipGrid;

  @Input() separatorKeysCodes: SeparatorKeyCodes[] = [
    SeparatorKeyCodes.ENTER,
    SeparatorKeyCodes.COMMA,
  ];
  static nextId = 0;
  @HostBinding('attr.aria-describedby') describedBy = '';
  @HostBinding('class.mat-form-field-invalid') errorState = false;

  private _chipValues: string[] = [];
  @Input()
  get value(): string[] {
    return this._chipValues;
  }
  set value(val: string[] | null) {
    this._chipValues = val || [];
    this._updateInvalidChips();
    this._cd.markForCheck();
    this.stateChanges.next();
    this._updateErrorState();
  }

  @Input() placeholder = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() id = '';

  focused = false;
  controlType = 'chip-input';
  invalidChips: number[] = [];
  isAtMaxLength = signal(false);

  onChange = (_: any) => {};

  onTouched = () => {};
  stateChanges = new Subject<void>();

  readonly autofilled: boolean;
  readonly empty: boolean;
  readonly shouldLabelFloat: boolean;
  readonly userAriaDescribedBy: string;

  constructor() {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    this._focusMonitor
      .monitor(this._elementRef, true)
      .pipe(
        observeOn(asapScheduler),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((origin) => {
        this.focused = !!origin;
        this.stateChanges.next();
      });
    this._iconManager.addIcons([
      {
        name: 'close-xs',
        icon: iconCloseXs,
      },
    ]);
  }

  ngDoCheck() {
    if (this.ngControl) {
      this._updateErrorState();
    }
  }

  ngAfterViewInit() {
    if (this.ngControl?.control) {
      this._checkMaxChipLength(this.ngControl.control);
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  // MatFormFieldControl methods
  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    if (this.isAtMaxLength()) {
      const target = event.target as HTMLElement | null;
      if (!target?.closest('mat-chip-row')) {
        this._chipGrid._focusLastChip();
      }
    } else {
      this._chipInputRef?.nativeElement.focus();
    }
  }

  writeValue(value: string[]): void {
    this.value = value || [];
    this.stateChanges.next();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.stateChanges.next();
  }

  // Error handling and state
  private _updateErrorState(): void {
    const control = this.ngControl?.control as FormControl;
    if (control) {
      this.errorState = control.invalid && (control.dirty || control.touched);
    }
    this.stateChanges.next();
  }

  get hasErrors(): boolean {
    return this.errorState;
  }

  get errors(): { [key: string]: any } | null {
    const control = this.ngControl?.control;
    return control ? control.errors : null;
  }

  addChip(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (!value) {
      return;
    }

    const delimiters = this._getDelimitersRegex();
    const values = value
      .split(delimiters)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (values.length === 0) {
      return;
    }
    this._updateControl([...this.value, ...values]);
    this.onTouched();
    event.chipInput!.clear();
  }

  private _updateControl(newValue: string[]): void {
    this._chipValues = newValue;
    this.onChange(newValue);
    this.ngControl?.control?.setValue(newValue);
    this.ngControl?.control?.updateValueAndValidity();
    this._updateInvalidChips();
  }

  removeChip(index: number): void {
    if (!this.disabled && index >= 0) {
      const newValue: string[] = [...this._chipValues];
      newValue.splice(index, 1);

      this._updateControl(newValue);
    }
  }

  private _updateInvalidChips(): void {
    const control = this.ngControl?.control;
    if (!control?.validator) {
      this.invalidChips = [];
      this.isAtMaxLength.set(false);
      return;
    }

    const invalidIndices = new Set<number>();

    // Add indices from duplicate validation errors
    const errors = control.errors;

    if (errors?.[DUPLICATE_VALUE_ERROR] && errors?.[DUPLICATE_INDEX_ERROR]) {
      errors[DUPLICATE_INDEX_ERROR].forEach((index: number) =>
        invalidIndices.add(index)
      );
    }

    // Add indices from individual chip validation
    this._chipValues.forEach((chip, index) => {
      const tempControl = new FormControl([chip]);
      const validationErrors = control.validator!(tempControl);

      if (validationErrors && !validationErrors[DUPLICATE_VALUE_ERROR]) {
        invalidIndices.add(index);
      }
    });

    // Add indices from maxlength validation
    if (errors?.[MAX_LENGTH_ERROR]) {
      this._chipValues.forEach((chip, index) => {
        if (index > errors.maxlength?.requiredLength - 1) {
          invalidIndices.add(index);
        }
      });
    }

    this.invalidChips = Array.from(invalidIndices);

    this._checkMaxChipLength(control);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text');
    if (!pastedText) {
      return;
    }

    const delimiters = this._getDelimitersRegex();
    const values = pastedText
      .split(delimiters)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (values.length > 0) {
      this._updateControl([...this.value, ...values]);
    }
    this.onTouched();
  }

  private _getDelimitersRegex(): RegExp {
    const delimiterChars: string[] = [];

    this.separatorKeysCodes.forEach((keyCode) => {
      switch (keyCode) {
        case SeparatorKeyCodes.ENTER:
          delimiterChars.push('\n', '\r');
          break;
        case SeparatorKeyCodes.COMMA:
          delimiterChars.push(',');
          break;
        case SeparatorKeyCodes.SPACE:
          delimiterChars.push(' ', '\\s');
          break;
        case SeparatorKeyCodes.TAB:
          delimiterChars.push('\t');
          break;
        case SeparatorKeyCodes.SEMICOLON:
          delimiterChars.push(';');
          break;
      }
    });

    // Always include newline for paste operations
    if (!delimiterChars.includes('\n')) {
      delimiterChars.push('\n', '\r');
    }

    // Build regex pattern from delimiter characters
    const uniqueDelimiters = [...new Set(delimiterChars)];
    const pattern = `[${uniqueDelimiters
      .map((char) =>
        char === '\\s' ? '\\s' : char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      )
      .join('')}]+`;

    return new RegExp(pattern);
  }

  private _checkMaxChipLength(control: AbstractControl) {
    if (control.validator) {
      const testControl = new FormControl([...this._chipValues, '']);
      this.isAtMaxLength.set(
        !!control.validator(testControl)?.[MAX_LENGTH_ERROR]
      );
    }
  }
}
